/**
 * The header menu: appearance and motion.
 *
 * Folded away because each is chosen once. Demo mode lived here as a data
 * source switch and is gone entirely — the live control plane is the only
 * thing this Console has ever honestly shown.
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';

import { useStore, useStoreState } from '../store-context.js';
import type { ThemeChoice } from '../../store.js';
import { Button } from '@primer/react';

export function ViewOptions(): ReactNode {
  const store = useStore();
  const reduceMotion = useStoreState((state) => state.reduceMotion);
  const theme = useStoreState((state) => state.theme);

  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // A menu that stays open after you have moved on is a menu covering the thing
  // you moved on to.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (event: MouseEvent): void => {
      if (!menuRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className={open ? 'menu menu-open' : 'menu'} ref={menuRef}>
      <Button aria-expanded={open} onClick={() => setOpen((v) => !v)}>
        View options
      </Button>

      {open ? (
        <div className="menu-panel">
          <div className="field">
            <span className="field-label">Appearance</span>
            {/*
              Three radios rather than a switch: a two-state control has to
              invent a position for "whatever the system says", and inferring
              it from the current appearance converts following the system into
              a fixed choice the first time it is touched.
            */}
            <div className="choice-row" role="radiogroup" aria-label="Appearance">
              {(['system', 'light', 'dark'] as const).map((value) => (
                <label className="choice" key={value}>
                  <input
                    type="radio"
                    name="theme"
                    value={value}
                    checked={theme === value}
                    onChange={() => store.setTheme(value as ThemeChoice)}
                  />
                  {value === 'system' ? 'System' : value === 'light' ? 'Light' : 'Dark'}
                </label>
              ))}
            </div>
          </div>

          <div className="field field-inline">
            <input
              type="checkbox"
              id="reduce-motion-input"
              checked={reduceMotion}
              onChange={(event) => store.setReduceMotion(event.target.checked)}
            />
            <label htmlFor="reduce-motion-input">Reduce motion</label>
          </div>
        </div>
      ) : null}
    </div>
  );
}
